import React, { useRef, useState, useEffect, useMemo } from 'react';
import { FaPlus, FaMinus } from 'react-icons/fa6';
import Select, { MultiValue } from 'react-select';
import {
    fetchCampaignMetadata,
    fetchCampaignSearchFilters,
    fetchStates,
} from '~/services/campaign';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    useClearReportFilterParams,
    useReportFilters,
} from '~/context/ReportFiltersProvider';
import {
    ALL_AVAILABLE_COLUMNS,
    COLUMN_GROUPS,
    DEFAULT_VISIBLE_COLUMNS,
    isDefaultSelection,
    parseVisibleColumns,
} from './teamMemberColumns';
import ResetFiltersButton from './ResetFiltersButton';

export type AccordionItem = {
    id: number;
    active: number | null;
    handleToggle: (index: number) => void;
};

interface Option {
    value: string;
    label: string;
}

// Which diagnoses belong to which condition, so the Diagnosis dropdown can
// narrow to the selected conditions the way the Campaign report's does.
interface MedicalIssueOption extends Option {
    medicalReasonId: string;
}

// Which of the campaign's date columns the range below applies to; mirrors
// CAMPAIGN_STATUS_DATE_FIELDS on the backend. Each label names the date, not
// the status, because the control sits under a "date range" heading and the
// bare status names read as a status filter.
//
// 'funded' is the status the rest of the app shows as "Open" (the DB has
// code 'funded' -> name 'Open'), so its label carries both.
const STATUS_DATE_FIELD_OPTIONS: Option[] = [
    { value: 'created', label: 'Created date' },
    { value: 'unpublished', label: 'Unpublished date' },
    { value: 'pend_financial_need', label: 'Pending Financial Need date' },
    { value: 'approved', label: 'Approved date' },
    { value: 'funded', label: 'Open (funded) date' },
    { value: 'disbursable', label: 'Disbursable date' },
    { value: 'closed', label: 'Closed date' },
    { value: 'reopened', label: 'Re-opened date' },
    { value: 'first_donation', label: 'First donation date' },
];

const EMAIL_PROVIDED_OPTIONS: Option[] = [
    { value: 'true', label: 'Email address provided' },
    { value: 'false', label: 'No email address' },
];

const PRIVACY_OPTIONS: Option[] = [
    { value: 'public', label: 'Public' },
    { value: 'private', label: 'Private' },
];

const DECEASED_OPTIONS: Option[] = [
    { value: 'false', label: 'Living clients only' },
    { value: 'true', label: 'Deceased clients only' },
];

const LIVING_DONOR_OPTIONS: Option[] = [
    { value: 'true', label: 'Living donor' },
    { value: 'false', label: 'Not a living donor' },
];

const EMPTY_FORM_VALUES = {
    zip_code: '',
    patient_zip_code: '',
    email_provided: '',
    privacy: '',
    deceased: '',
    living_donor: '',
    status_date_field: '',
    status_date_start: '',
    status_date_end: '',
    csc: '',
    transplant_diagnosis: '',
    candidacy_status: '',
    medical_issue_start_date: '',
    medical_issue_end_date: '',
    listing_start_date: '',
    listing_end_date: '',
};

type FormValues = typeof EMPTY_FORM_VALUES;

const selectClassNames = {
    control: () => 'w-full rounded-[4px] border border-[#949599] text-[16px]/[20px]',
    placeholder: () => 'text-[#949599]',
    multiValue: () => 'bg-[#F4F4F5] rounded-[4px] px-2 py-1 mr-1 mb-1',
    multiValueLabel: () => 'text-[14px]',
    multiValueRemove: () => 'hover:bg-[#F02D3A] hover:text-white rounded-r-[4px] px-1',
};

const labelClass = 'block text-[12px]/[14px] font-extrabold';
const inputClass =
    'w-full rounded-[4px] border border-[#949599] p-[10px] text-[16px]/[20px]';

const TeamMemberAccordionItem: React.FC<AccordionItem> = ({
    active,
    handleToggle,
    id,
}) => {
    const contentEl = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setFilterParams, setActiveAccordion } = useReportFilters();
    const clearReportFilterParams = useClearReportFilterParams();

    const [formValues, setFormValues] = useState<FormValues>(EMPTY_FORM_VALUES);

    // Option lists
    const [roleOptions, setRoleOptions] = useState<Option[]>([]);
    const [relationshipOptions, setRelationshipOptions] = useState<Option[]>([]);
    const [stateOptions, setStateOptions] = useState<Option[]>([]);
    const [statusOptions, setStatusOptions] = useState<Option[]>([]);
    const [regionOptions, setRegionOptions] = useState<Option[]>([]);
    const [cscOptions, setCscOptions] = useState<Option[]>([]);
    const [medicalReasonOptions, setMedicalReasonOptions] = useState<Option[]>([]);
    const [medicalIssueOptions, setMedicalIssueOptions] = useState<
        MedicalIssueOption[]
    >([]);
    const [transplantDiagnosisOptions, setTransplantDiagnosisOptions] = useState<
        Option[]
    >([]);
    const [candidacyStatusOptions, setCandidacyStatusOptions] = useState<Option[]>(
        [],
    );
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);

    // Multi-select selections
    const [selectedRoles, setSelectedRoles] = useState<Option[]>([]);
    const [selectedRelationships, setSelectedRelationships] = useState<Option[]>([]);
    const [selectedStates, setSelectedStates] = useState<Option[]>([]);
    const [selectedPatientStates, setSelectedPatientStates] = useState<Option[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<Option[]>([]);
    const [selectedRegions, setSelectedRegions] = useState<Option[]>([]);
    const [selectedMedicalReasons, setSelectedMedicalReasons] = useState<Option[]>(
        [],
    );
    const [selectedMedicalIssues, setSelectedMedicalIssues] = useState<Option[]>([]);

    // Column picker: seed from the URL so Back-to-Search round-trips keep the
    // selection; otherwise fall back to defaults.
    const [showColumns, setShowColumns] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState<string[]>(() =>
        parseVisibleColumns(searchParams.get('visible_columns')),
    );

    const toggleColumn = (key: string) => {
        setSelectedColumns(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key],
        );
    };

    // Diagnoses narrow to the selected conditions; with none selected, show all.
    const availableMedicalIssues = useMemo(() => {
        if (!selectedMedicalReasons.length) return medicalIssueOptions;
        const reasonIds = new Set(selectedMedicalReasons.map(r => r.value));
        return medicalIssueOptions.filter(issue =>
            reasonIds.has(issue.medicalReasonId),
        );
    }, [medicalIssueOptions, selectedMedicalReasons]);

    // Drop any diagnosis that the narrowed list no longer offers, so the
    // search can't send a diagnosis that contradicts the condition filter.
    useEffect(() => {
        const available = new Set(availableMedicalIssues.map(i => i.value));
        setSelectedMedicalIssues(prev =>
            prev.filter(issue => available.has(issue.value)),
        );
    }, [availableMedicalIssues]);

    useEffect(() => {
        const loadOptions = async () => {
            setIsLoadingOptions(true);
            try {
                const [filters, metadata, states] = await Promise.all([
                    fetchCampaignSearchFilters(),
                    fetchCampaignMetadata(),
                    fetchStates(),
                ]);

                if (filters?.team_member_groups) {
                    setRoleOptions(
                        filters.team_member_groups.map(
                            (group: { id: number; name: string }) => ({
                                value: String(group.id),
                                label: group.name,
                            }),
                        ),
                    );
                }
                if (filters?.campaign_statuses) {
                    setStatusOptions(
                        filters.campaign_statuses.map(
                            (status: { code: string; name: string }) => ({
                                value: status.code,
                                label: status.name,
                            }),
                        ),
                    );
                }
                if (filters?.campaign_regions) {
                    setRegionOptions(
                        filters.campaign_regions.map(
                            (region: { code: string; name: string }) => ({
                                value: region.code,
                                label: region.name,
                            }),
                        ),
                    );
                }

                if (Array.isArray(states)) {
                    setStateOptions(
                        states.map((state: { abbreviation: string; state: string }) => ({
                            value: state.abbreviation,
                            label: state.state,
                        })),
                    );
                }

                if (metadata?.campaign_is_for) {
                    // The endpoint prefixes the list with an empty entry for
                    // the "not specified" option on edit forms; a filter has
                    // no use for it. 'Self' is the client's own row, which is
                    // never a team member.
                    setRelationshipOptions(
                        metadata.campaign_is_for
                            .filter(
                                (rel: { id?: number; code?: string; name?: string }) =>
                                    rel && rel.id && rel.code !== 'self',
                            )
                            .map((rel: { id: number; name: string }) => ({
                                value: String(rel.id),
                                label: rel.name,
                            })),
                    );
                }
                if (metadata?.patient_services_coordinators) {
                    setCscOptions(
                        metadata.patient_services_coordinators.map(
                            (psc: { id: number; full_name: string }) => ({
                                value: String(psc.id),
                                label: psc.full_name,
                            }),
                        ),
                    );
                }
                if (metadata?.medical_reasons) {
                    setMedicalReasonOptions(
                        metadata.medical_reasons.map(
                            (reason: { id: number; name: string }) => ({
                                value: String(reason.id),
                                label: reason.name,
                            }),
                        ),
                    );
                }
                if (metadata?.medical_issues) {
                    setMedicalIssueOptions(
                        metadata.medical_issues.map(
                            (issue: {
                                id: number;
                                name: string;
                                medical_reason?: { id: number } | null;
                            }) => ({
                                value: String(issue.id),
                                label: issue.name,
                                medicalReasonId: issue.medical_reason
                                    ? String(issue.medical_reason.id)
                                    : '',
                            }),
                        ),
                    );
                }
                if (metadata?.transplant_diagnoses) {
                    setTransplantDiagnosisOptions(
                        metadata.transplant_diagnoses.map(
                            (diagnosis: { id: number; name: string }) => ({
                                value: String(diagnosis.id),
                                label: diagnosis.name,
                            }),
                        ),
                    );
                }
                if (metadata?.candidacy_statuses) {
                    setCandidacyStatusOptions(
                        metadata.candidacy_statuses.map(
                            (candidacy: { id: number; name: string }) => ({
                                value: String(candidacy.id),
                                label: candidacy.name,
                            }),
                        ),
                    );
                }
            } catch (error) {
                console.error('Error loading Team Member report filters:', error);
            } finally {
                setIsLoadingOptions(false);
            }
        };

        loadOptions();
    }, []);

    // Re-hydrate the form from the URL, so Back to Search returns the search
    // that produced the results rather than an empty form.
    useEffect(() => {
        if (searchParams.size === 0) return;

        setFormValues({
            zip_code: searchParams.get('zip_code') || '',
            patient_zip_code: searchParams.get('patient_zip_code') || '',
            email_provided: searchParams.get('email_provided') || '',
            privacy: searchParams.get('privacy') || '',
            deceased: searchParams.get('deceased') || '',
            living_donor: searchParams.get('living_donor') || '',
            status_date_field: searchParams.get('status_date_field') || '',
            status_date_start: searchParams.get('status_date_start') || '',
            status_date_end: searchParams.get('status_date_end') || '',
            csc: searchParams.get('csc') || '',
            transplant_diagnosis: searchParams.get('transplant_diagnosis') || '',
            candidacy_status: searchParams.get('candidacy_status') || '',
            medical_issue_start_date:
                searchParams.get('medical_issue_start_date') || '',
            medical_issue_end_date: searchParams.get('medical_issue_end_date') || '',
            listing_start_date: searchParams.get('listing_start_date') || '',
            listing_end_date: searchParams.get('listing_end_date') || '',
        });

        const restore = (
            param: string,
            options: Option[],
            setter: (opts: Option[]) => void,
        ) => {
            const values = searchParams.getAll(param);
            if (values.length && options.length) {
                setter(options.filter(opt => values.includes(opt.value)));
            }
        };

        restore('role', roleOptions, setSelectedRoles);
        restore('relationship', relationshipOptions, setSelectedRelationships);
        restore('state', stateOptions, setSelectedStates);
        restore('patient_state', stateOptions, setSelectedPatientStates);
        restore('campaign_status', statusOptions, setSelectedStatuses);
        restore('region', regionOptions, setSelectedRegions);
        restore('medical_reason', medicalReasonOptions, setSelectedMedicalReasons);
        restore('medical_issue', medicalIssueOptions, setSelectedMedicalIssues);
    }, [
        searchParams,
        roleOptions,
        relationshipOptions,
        stateOptions,
        statusOptions,
        regionOptions,
        medicalReasonOptions,
        medicalIssueOptions,
    ]);

    const setValue = (key: keyof FormValues) => (value: string) =>
        setFormValues(prev => ({ ...prev, [key]: value }));

    const hasActiveFilters =
        Object.values(formValues).some(value => value !== '') ||
        selectedRoles.length > 0 ||
        selectedRelationships.length > 0 ||
        selectedStates.length > 0 ||
        selectedPatientStates.length > 0 ||
        selectedStatuses.length > 0 ||
        selectedRegions.length > 0 ||
        selectedMedicalReasons.length > 0 ||
        selectedMedicalIssues.length > 0;

    const handleResetFilters = () => {
        setFormValues(EMPTY_FORM_VALUES);
        setSelectedRoles([]);
        setSelectedRelationships([]);
        setSelectedStates([]);
        setSelectedPatientStates([]);
        setSelectedStatuses([]);
        setSelectedRegions([]);
        setSelectedMedicalReasons([]);
        setSelectedMedicalIssues([]);
        clearReportFilterParams();
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();

        const params = new URLSearchParams();

        (Object.keys(formValues) as (keyof FormValues)[]).forEach(key => {
            const value = formValues[key];
            if (value) params.append(key, value);
        });

        const appendAll = (param: string, options: Option[]) =>
            options.forEach(option => params.append(param, option.value));

        appendAll('role', selectedRoles);
        appendAll('relationship', selectedRelationships);
        appendAll('state', selectedStates);
        appendAll('patient_state', selectedPatientStates);
        appendAll('campaign_status', selectedStatuses);
        appendAll('region', selectedRegions);
        appendAll('medical_reason', selectedMedicalReasons);
        appendAll('medical_issue', selectedMedicalIssues);

        // Only carry the column selection when it differs from the defaults,
        // keeping the common-case URL clean.
        if (!isDefaultSelection(selectedColumns) && selectedColumns.length) {
            params.append('visible_columns', selectedColumns.join(','));
        }

        setFilterParams(params.toString());
        setActiveAccordion(id);
        navigate(`/reports/team-members?${params.toString()}`);
    };

    const singleSelect = (
        key: keyof FormValues,
        options: Option[],
        placeholder: string,
        isLoading = false,
    ) => (
        <Select
            menuPlacement='auto'
            options={options}
            isClearable
            isLoading={isLoading}
            value={options.find(opt => opt.value === formValues[key]) || null}
            onChange={option => setValue(key)((option as Option | null)?.value || '')}
            placeholder={placeholder}
            classNames={selectClassNames}
        />
    );

    const multiSelect = (
        options: Option[],
        value: Option[],
        onChange: (opts: Option[]) => void,
        placeholder: string,
        isLoading = false,
    ) => (
        <Select
            menuPlacement='auto'
            options={options}
            isMulti
            isLoading={isLoading}
            value={value}
            onChange={(selected: MultiValue<Option>) => onChange([...selected])}
            placeholder={placeholder}
            classNames={selectClassNames}
        />
    );

    const dateRange = (
        startKey: keyof FormValues,
        endKey: keyof FormValues,
    ) => (
        <div className='flex items-center gap-2'>
            <input
                type='date'
                value={formValues[startKey]}
                onChange={e => setValue(startKey)(e.target.value)}
                className='rounded-[4px] border border-[#949599] p-[10px]'
            />
            <span className='text-gray-500'>to</span>
            <input
                type='date'
                value={formValues[endKey]}
                onChange={e => setValue(endKey)(e.target.value)}
                className='rounded-[4px] border border-[#949599] p-[10px]'
            />
        </div>
    );

    return (
        <div
            className={`w-full max-w-[932px] rounded-sm border bg-white px-7.5 py-4 shadow-default ${
                active === id ? 'border-primary' : 'border-stroke'
            }`}
        >
            <button
                className={`flex w-full items-center justify-between gap-2 ${
                    active === id ? 'active' : ''
                }`}
                onClick={() => handleToggle(id)}
            >
                <div>
                    <h4 className='text-title-xsm font-bold sm:text-title-md'>
                        Team Member
                    </h4>
                </div>
                <div className='flex items-center gap-2'>
                    <FaPlus className={`${active === id ? 'hidden' : ''}`} />
                    <FaMinus className={`${active === id ? 'block' : 'hidden'}`} />
                </div>
            </button>

            <div
                ref={contentEl}
                className={`py-5 duration-200 ease-in-out ${
                    active === id ? 'block' : 'hidden'
                }`}
            >
                <form onSubmit={handleSearch}>
                    <div className='flex flex-wrap gap-[30px] lg:flex-nowrap'>
                        <div className='w-full lg:w-3/5'>
                            <h2 className='text-[#949599] text-[18px/22px]'>
                                TEAM MEMBER INFORMATION
                            </h2>

                            <div className='mt-4'>
                                <label className={labelClass}>Role</label>
                                {multiSelect(
                                    roleOptions,
                                    selectedRoles,
                                    setSelectedRoles,
                                    'Any role',
                                    isLoadingOptions,
                                )}
                            </div>

                            <div className='mt-4'>
                                <label className={labelClass}>
                                    Relationship to Client
                                </label>
                                {multiSelect(
                                    relationshipOptions,
                                    selectedRelationships,
                                    setSelectedRelationships,
                                    'Any relationship',
                                    isLoadingOptions,
                                )}
                            </div>

                            <div className='mt-4'>
                                <label className={labelClass}>Email Provided</label>
                                {singleSelect(
                                    'email_provided',
                                    EMAIL_PROVIDED_OPTIONS,
                                    'Any email value',
                                )}
                            </div>

                            <div className='mt-4 flex flex-wrap items-end gap-4'>
                                <div className='min-w-[200px] flex-1'>
                                    <label className={labelClass}>State</label>
                                    {multiSelect(
                                        stateOptions,
                                        selectedStates,
                                        setSelectedStates,
                                        'Any state',
                                        isLoadingOptions,
                                    )}
                                </div>
                                <div className='min-w-[140px] flex-1'>
                                    <label className={labelClass}>Zip Code</label>
                                    <input
                                        type='text'
                                        value={formValues.zip_code}
                                        onChange={e =>
                                            setValue('zip_code')(e.target.value)
                                        }
                                        placeholder='Any zip code'
                                        className={inputClass}
                                    />
                                </div>
                            </div>

                            <h2 className='mt-8 text-[#949599] text-[18px/22px]'>
                                CAMPAIGN
                            </h2>

                            <div className='mt-4'>
                                <label className={labelClass}>Campaign Status</label>
                                {multiSelect(
                                    statusOptions,
                                    selectedStatuses,
                                    setSelectedStatuses,
                                    'Any status',
                                    isLoadingOptions,
                                )}
                            </div>

                            <div className='mt-4'>
                                <label className={labelClass}>
                                    Status Date Range
                                </label>
                                <p className='mb-1 text-[12px] text-gray-600'>
                                    Finds campaigns that reached a state inside
                                    the range, whatever state they are in now.
                                </p>
                                <div className='flex flex-wrap items-center gap-2'>
                                    <div className='w-64'>
                                        <Select
                                            menuPlacement='auto'
                                            options={STATUS_DATE_FIELD_OPTIONS}
                                            isClearable
                                            value={
                                                STATUS_DATE_FIELD_OPTIONS.find(
                                                    opt =>
                                                        opt.value ===
                                                        formValues.status_date_field,
                                                ) || null
                                            }
                                            onChange={option =>
                                                setValue('status_date_field')(
                                                    (option as Option | null)
                                                        ?.value || '',
                                                )
                                            }
                                            placeholder='Which date?'
                                            classNames={selectClassNames}
                                        />
                                    </div>
                                    {dateRange('status_date_start', 'status_date_end')}
                                </div>
                            </div>

                            <div className='mt-4 flex flex-wrap items-end gap-4'>
                                <div className='min-w-[200px] flex-1'>
                                    <label className={labelClass}>
                                        Privacy Status
                                    </label>
                                    {singleSelect(
                                        'privacy',
                                        PRIVACY_OPTIONS,
                                        'Any privacy status',
                                    )}
                                </div>
                                <div className='min-w-[200px] flex-1'>
                                    <label className={labelClass}>
                                        Living / Deceased
                                    </label>
                                    {singleSelect(
                                        'deceased',
                                        DECEASED_OPTIONS,
                                        'Include all clients',
                                    )}
                                </div>
                            </div>

                            <div className='mt-4 flex flex-wrap items-end gap-4'>
                                <div className='min-w-[200px] flex-1'>
                                    <label className={labelClass}>Region</label>
                                    {multiSelect(
                                        regionOptions,
                                        selectedRegions,
                                        setSelectedRegions,
                                        'Any region',
                                        isLoadingOptions,
                                    )}
                                </div>
                                <div className='min-w-[200px] flex-1'>
                                    <label className={labelClass}>
                                        Client Services Coordinator
                                    </label>
                                    {singleSelect(
                                        'csc',
                                        cscOptions,
                                        'Any coordinator',
                                        isLoadingOptions,
                                    )}
                                </div>
                            </div>

                            <div className='mt-4 flex flex-wrap items-end gap-4'>
                                <div className='min-w-[200px] flex-1'>
                                    <label className={labelClass}>Client State</label>
                                    {multiSelect(
                                        stateOptions,
                                        selectedPatientStates,
                                        setSelectedPatientStates,
                                        'Any state',
                                        isLoadingOptions,
                                    )}
                                </div>
                                <div className='min-w-[140px] flex-1'>
                                    <label className={labelClass}>
                                        Client Zip Code
                                    </label>
                                    <input
                                        type='text'
                                        value={formValues.patient_zip_code}
                                        onChange={e =>
                                            setValue('patient_zip_code')(
                                                e.target.value,
                                            )
                                        }
                                        placeholder='Any zip code'
                                        className={inputClass}
                                    />
                                </div>
                            </div>

                            <h2 className='mt-8 text-[#949599] text-[18px/22px]'>
                                MEDICAL INFORMATION
                            </h2>

                            <div className='mt-4'>
                                <label className={labelClass}>Condition</label>
                                {multiSelect(
                                    medicalReasonOptions,
                                    selectedMedicalReasons,
                                    setSelectedMedicalReasons,
                                    'Any medical reason',
                                    isLoadingOptions,
                                )}
                            </div>

                            <div className='mt-4'>
                                <label className={labelClass}>Diagnosis</label>
                                {multiSelect(
                                    availableMedicalIssues,
                                    selectedMedicalIssues,
                                    setSelectedMedicalIssues,
                                    'Any diagnosis',
                                    isLoadingOptions,
                                )}
                            </div>

                            <div className='mt-4 flex flex-wrap items-end gap-4'>
                                <div className='min-w-[200px] flex-1'>
                                    <label className={labelClass}>
                                        Transplant Diagnosis
                                    </label>
                                    {singleSelect(
                                        'transplant_diagnosis',
                                        transplantDiagnosisOptions,
                                        'Any diagnosis',
                                        isLoadingOptions,
                                    )}
                                </div>
                                <div className='min-w-[200px] flex-1'>
                                    <label className={labelClass}>
                                        Candidacy Status
                                    </label>
                                    {singleSelect(
                                        'candidacy_status',
                                        candidacyStatusOptions,
                                        'Any candidacy status',
                                        isLoadingOptions,
                                    )}
                                </div>
                            </div>

                            <div className='mt-4'>
                                <label className={labelClass}>Living Donor</label>
                                {singleSelect(
                                    'living_donor',
                                    LIVING_DONOR_OPTIONS,
                                    'Any',
                                )}
                            </div>

                            <div className='mt-4'>
                                <label className={labelClass}>
                                    Medical Issue Date Range
                                </label>
                                {dateRange(
                                    'medical_issue_start_date',
                                    'medical_issue_end_date',
                                )}
                            </div>

                            <div className='mt-4'>
                                <label className={labelClass}>
                                    Listing Date Range
                                </label>
                                {dateRange('listing_start_date', 'listing_end_date')}
                            </div>
                        </div>

                        <div className='w-full lg:w-2/5'>
                            <div className='w-full rounded bg-[#F4F4F5] p-5'>
                                <h2 className='text-[#949599] text-[18px/22px]'>
                                    ABOUT THIS SEARCH
                                </h2>
                                <p className='mt-3 text-sm text-gray-600'>
                                    One row per team member on a client campaign.
                                    Filter by the team member's own details, by the
                                    campaign they sit on, or by the client's medical
                                    information.
                                </p>
                                <p className='mt-3 text-sm text-gray-600'>
                                    <strong>Role</strong> is the team member's access
                                    level:
                                </p>
                                <ul className='mt-2 list-inside list-disc text-sm text-gray-600'>
                                    <li>
                                        <strong>Full</strong> - full access to the
                                        campaign on the website
                                    </li>
                                    <li>
                                        <strong>Limited</strong> - limited access to
                                        the campaign on the website
                                    </li>
                                    <li>
                                        <strong>No access</strong> - no login, but
                                        still on the campaign's team
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Column Configuration */}
                    <div className='mt-8 border-t border-gray-200 pt-6'>
                        <div className='flex items-center justify-between'>
                            <button
                                type='button'
                                onClick={() => setShowColumns(s => !s)}
                                className='flex items-center gap-2 text-[12px] font-bold text-[#949599]'
                            >
                                COLUMNS ({selectedColumns.length} selected)
                                {showColumns ? (
                                    <FaMinus size={10} />
                                ) : (
                                    <FaPlus size={10} />
                                )}
                            </button>
                            {!isDefaultSelection(selectedColumns) && (
                                <button
                                    type='button'
                                    onClick={() =>
                                        setSelectedColumns(DEFAULT_VISIBLE_COLUMNS)
                                    }
                                    className='text-[12px] text-gray-500 underline hover:text-gray-700'
                                >
                                    Reset to default
                                </button>
                            )}
                        </div>
                        {showColumns && (
                            <div className='mt-4 grid grid-cols-1 gap-6 md:grid-cols-3'>
                                {COLUMN_GROUPS.map(group => (
                                    <div key={group}>
                                        <h3 className='mb-2 text-[12px] font-bold text-gray-700'>
                                            {group}
                                        </h3>
                                        <div className='flex flex-col gap-2'>
                                            {ALL_AVAILABLE_COLUMNS.filter(
                                                column => column.group === group,
                                            ).map(column => (
                                                <label
                                                    key={column.key}
                                                    className='flex cursor-pointer items-center gap-2 text-sm'
                                                >
                                                    <input
                                                        type='checkbox'
                                                        checked={selectedColumns.includes(
                                                            column.key,
                                                        )}
                                                        onChange={() =>
                                                            toggleColumn(column.key)
                                                        }
                                                        className='rounded border-gray-300 text-primary focus:ring-primary'
                                                    />
                                                    <span className='text-gray-700'>
                                                        {column.label}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className='mt-8 flex flex-wrap justify-center gap-4'>
                        <ResetFiltersButton
                            onReset={handleResetFilters}
                            disabled={!hasActiveFilters}
                        />
                        <button
                            type='submit'
                            className='rounded-lg bg-primary px-8 py-3 text-center text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/50'
                        >
                            Search Team Members
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TeamMemberAccordionItem;
